import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { OAuthService } from 'angular-oauth2-oidc';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { NotificationPreference } from '../model/notification-preference.model';
import {
    GlobalSuiteConfig,
    UserCredential,
    UserIdentity,
} from '../model/user-identity.model';
import { apiConstant } from '../utility/constant/api-constant';
import { applicationAccessConstant, routes } from '../utility/constant/config';

type aTokenType = {
    email: string;
    givenName: string;
    groups: string[];
    lastname: string;
    member_of: string[];
    name: string;
    sub: string;
};

type iTokenType = {
    aud: string;
};

export interface GeneralUserRole {
    role: string;
    actions: string[];
}

export interface FetchUserRolePermissionsResponse {
    is_success: boolean;
    message: string;
    data: {
        user_role: { [key: string]: string[] };
    };
}

@Injectable({ providedIn: 'root' })
export class UserService {
    private accessToken = '';
    private readonly apiEndpoint = import.meta.env.NG_APP_API_URL;
    private idToken = '';
    private issuer = '';
    private userGroup: string[] = [];
    public _identityEstablished$: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);
    public _pageConfig$: BehaviorSubject<GlobalSuiteConfig[] | null> =
        new BehaviorSubject<GlobalSuiteConfig[] | null>(null);
    public _sessionExpiredCommand$: BehaviorSubject<null> =
        new BehaviorSubject<null>(null);
    public _userCredential$: BehaviorSubject<UserCredential | null> =
        new BehaviorSubject<UserCredential | null>(null);
    public _userIdentity$: BehaviorSubject<UserIdentity | null> =
        new BehaviorSubject<UserIdentity | null>(null);
    private readonly _notificationPreference$ =
        new BehaviorSubject<NotificationPreference>('MENTIONS_ONLY');
    public notificationPreference$ =
        this._notificationPreference$.asObservable();
    public appAPDAccess_Found = false;
    public appSILAccess_Found = false;

    protected applicationAccessDenied = false;
    public identityEstablished = this._identityEstablished$.asObservable();
    public isAdminSDSUser = false;
    public pageConfig = this._pageConfig$.asObservable();
    public sessionExpiredCommand = this._sessionExpiredCommand$.asObservable();
    public userCredential = this._userCredential$.asObservable();
    public userId = '';
    public userIdentity = this._userIdentity$.asObservable();

    private readonly generalUserRoles = new BehaviorSubject<GeneralUserRole[]>(
        []
    );
    generalUserRoles$ = this.generalUserRoles.asObservable();

    constructor(
        private readonly oAuthService: OAuthService,
        private readonly http: HttpClient,
        private readonly router: Router
    ) {
        this.parseSuiteConfiguration();
    }

    _getUsergroup() {
        return this.userGroup;
    }

    appRedirectToLanding() {
        if (import.meta.env.NG_APP_LANDING_PAGE_URL?.length) {
            globalThis.location.replace(
                import.meta.env.NG_APP_LANDING_PAGE_URL + '/entry'
            );
        }
    }

    get applicationAccess() {
        return this.applicationAccessDenied;
    }

    calculateApplicationAccess(element: any, userGroups: string[]) {
        element.hasAccess = true;
        if (element.code === 'SDS') {
            this.applicationAccessDenied = true;
            this.isAdminSDSUser = !!userGroups.some((group: string) => {
                return !!applicationAccessConstant.sdtAdminGoupAccessName.some(
                    (accessName: string) => {
                        return group.startsWith(accessName);
                    }
                );
            });
        }
        if (element.code === 'SIL') {
            this.appSILAccess_Found = true;
        }
        if (element.code === 'APD') {
            this.appAPDAccess_Found = true;
        }
    }

    public establishSession(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (this.oAuthService.hasValidAccessToken()) {
                const intervelTime = 5000;
                const interval = setInterval(() => {
                    if (!this.oAuthService.hasValidAccessToken()) {
                        clearInterval(interval);
                        this.sessionExpiredCommandRequest();
                    }
                }, intervelTime);
                try {
                    this.instantiate(
                        this.oAuthService.getAccessToken(),
                        this.oAuthService.getIdToken(),
                        this.oAuthService.getAccessTokenExpiration()
                    );
                    resolve(true);
                    return;
                } catch (ex) {
                    console.error('Error during instantiation:', ex);
                    clearInterval(interval);
                    reject(new Error('Instantiation failed'));
                    return;
                }
            }
            reject(new Error('No valid access token found'));
        });
    }

    getAppLogoutUrl() {
        let appBaseUrl = import.meta.env.NG_APP_LANDING_PAGE_URL;
        if (appBaseUrl) {
            const parsedUrl = new URL(appBaseUrl);
            appBaseUrl = `${parsedUrl.protocol}//${parsedUrl.host}/logout`;
        }
        return appBaseUrl;
    }

    public getUserCredential() {
        return {
            accessToken: this.accessToken,
            idToken: this.idToken,
            issuer: this.issuer,
        };
    }

    public instantiate(
        accessToken: string,
        idToken: string,
        expires: number
    ): void {
        const aToken: aTokenType = jwtDecode(accessToken);
        const iToken: iTokenType = jwtDecode(idToken);

        const parseStringCnEntry = (groupEntry: string) => {
            if (groupEntry.includes(',') || groupEntry.includes('=')) {
                let found = '';
                const commaSeparated = groupEntry.split(',');
                commaSeparated.forEach((groupProperty: string) => {
                    const equalParsed = groupProperty.split('=');
                    if (equalParsed[0] === 'CN') {
                        found = equalParsed[1];
                    }
                });
                return found;
            } else {
                return groupEntry;
            }
        };

        const parseCnGroups = (groupsArray: string[]): string[] => {
            const parsed: string[] = [];
            if (!groupsArray) {
                return parsed;
            }
            if (!!groupsArray && !Array.isArray(groupsArray)) {
                parsed.push(parseStringCnEntry(groupsArray));
                return parsed;
            }
            groupsArray.forEach((groupEntry: string) => {
                parsed.push(parseStringCnEntry(groupEntry));
            });
            return parsed;
        };

        const userGroups: string[] = aToken.groups
            ? aToken.groups
            : parseCnGroups(aToken.member_of);

        this.applicationAccessDenied = false;
        this.appSILAccess_Found = false;
        this.appAPDAccess_Found = false;

        this.checkApplicationAccess(userGroups);

        this._identityEstablished$.next(true);

        this._userIdentity$.next({
            email: aToken.email,
            givenName: aToken.givenName,
            lastName: aToken.lastname,
            name: aToken.name,
            sub: aToken.sub,
        });
        this._userCredential$.next({
            accessToken,
            clientId: iToken.aud,
            expiresIn: expires,
            idToken,
            relevantGroup: userGroups,
        });
    }

    public logout(expired = false) {
        sessionStorage.clear();
        this._identityEstablished$.next(false);
        this._userIdentity$.next(null);
        this._userCredential$.next(null);
        if (expired) {
            sessionStorage.setItem('justExpired', 'true');
        }
        this.oAuthService.revokeTokenAndLogout({
            client_id: this.oAuthService.clientId,
            returnTo: this.oAuthService.redirectUri,
        });
        this.oAuthService.logOut();
        const landingPageLogoutUrl = this.getAppLogoutUrl();
        if (landingPageLogoutUrl) {
            globalThis.location.replace(landingPageLogoutUrl);
        } else {
            this.router.navigate([routes.loginURL], { replaceUrl: true });
        }
    }

    public parseSuiteConfiguration() {
        const pageConfig: GlobalSuiteConfig[] | null = JSON.parse(
            import.meta.env.NG_APP_FE_SUITE
        );
        if (pageConfig) {
            if (pageConfig.length <= 0) {
                throw new Error('Empty config set');
            }
            for (const config of pageConfig) {
                if (config.code.length <= 0) {
                    throw new Error('Config entry is missing app code');
                }
                if (config.redirectUrl.length <= 0) {
                    throw new Error('Config entry is missing redirect url');
                }
            }
            this._pageConfig$.next(pageConfig);
        } else {
            throw new Error('Unknown error: Config is missing');
        }
    }

    saveUserAPI(): Observable<any> {
        return this.http.post(this.apiEndpoint + apiConstant.saveUser, null);
    }

    saveUser(): Observable<any> {
        return of('ok');
    }

    updateNotificationPreference(
        preference: NotificationPreference
    ): Observable<any> {
        return this.http
            .put(this.apiEndpoint + apiConstant.updateNotificationPreference, {
                notification_preference: preference,
            })
            .pipe(
                tap((response: any) => {
                    if (response?.is_success) {
                        this._notificationPreference$.next(preference);
                    }
                })
            );
    }

    getNotificationPreference(): NotificationPreference {
        return this._notificationPreference$.getValue();
    }

    setNotificationPreference(preference: NotificationPreference): void {
        this._notificationPreference$.next(preference);
    }

    public sessionExpiredCommandRequest() {
        this._sessionExpiredCommand$.next(null);
    }

    public setUserCredential(
        accessToken: string,
        idToken: string,
        issuer: string
    ) {
        this.accessToken = accessToken;
        this.idToken = idToken;
        this.issuer = issuer;
    }

    setUserGroup(userGroup: string[]) {
        this.userGroup = userGroup;
    }

    private checkApplicationAccess(userGroups: string[]) {
        if (userGroups && this._pageConfig$.value) {
            for (const element of this._pageConfig$.value) {
                let foundForEntry = false;
                element.requiredGroup.forEach((group) => {
                    if (group.includes('*')) {
                        userGroups.forEach((userGroup) => {
                            const split = group.split('*');
                            if (
                                userGroup.includes(split[0]) &&
                                userGroup.includes(split[1])
                            ) {
                                foundForEntry = true;
                            }
                        });
                    } else if (userGroups.includes(group)) {
                        foundForEntry = true;
                    }
                });
                if (foundForEntry) {
                    this.calculateApplicationAccess(element, userGroups);
                }
            }
        }
    }

    fetchUserRolePermissions(): Observable<FetchUserRolePermissionsResponse> {
        return this.http
            .get<FetchUserRolePermissionsResponse>(
                this.apiEndpoint + apiConstant.fetchUserRolePermissions
                // '../../assets/fetch-user-role-permissions.json'
            )
            .pipe(
                tap((response) => {
                    if (response.is_success && response.data?.user_role) {
                        const roles: GeneralUserRole[] = Object.entries(
                            response.data.user_role
                        ).map(([role, actions]) => ({ role, actions }));
                        this.generalUserRoles.next(roles);
                    }
                }),
                catchError((err: any) => throwError(() => err))
            );
    }
}
